import React, { useContext, useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Handle, useReactFlow } from "react-flow-renderer";
import { NodesContext } from "../provider/IvrContext";
import AudioPreviewModal from "../Helpers/AudioPreviewModel";
import { toast } from "react-toastify";
import {
    BACKEND_API_BASE_URL,
    VOICEMAIL_PROMPT_CATEGORIES,
    VOICEMAIL_MAX_DURATION,
    VOICEMAIL_MIN_DURATION
} from "../../../../../utilities/constant";
import { getApiCall } from "../../../../../service/httpCall";


const VoicemailNode = ({ data, id }) => {
    const { setNodes } = useReactFlow();
    const { updateNodeData, onRemove } = useContext(NodesContext);

    const action = data.action || {};
    const maxDuration = data.max_duration || "";
    const promptCategory = action.prompt_category || "";
    const audioUrl = action.audio || "";
    const audioName = action.name || "";

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [playModalOpen, setPlayModalOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [promptList, setPromptList] = useState([]);

    const [draftAction, setDraftAction] = useState(action);
    const [draftDuration, setDraftDuration] = useState(maxDuration);
    const [draftCategory, setDraftCategory] = useState(promptCategory);
    const [draftUseMedia, setDraftUseMedia] = useState(Boolean(data.use_media));

    useEffect(() => {
        const fetchPrompts = async () => {
            try {
                const resp = await getApiCall(
                    `${BACKEND_API_BASE_URL}/v2/prompt?prompt_status=1&user_id=${data.userId}`
                );
                if (!Array.isArray(resp)) return;
                const filtered = resp.filter(p =>
                    VOICEMAIL_PROMPT_CATEGORIES.includes(p.prompt_category)
                );
                setPromptList(filtered);
            } catch (err) {
                console.error(err);
            }
        };
        if (data.userId) fetchPrompts();
    }, [data.userId]);

    const updateNode = (updates) => updateNodeData(id, updates);

    const openModal = () => {
        setDraftAction(action);
        setDraftDuration(maxDuration);
        setDraftCategory(promptCategory);
        setDraftUseMedia(Boolean(data.use_media));
        setErrors({});
        setModalIsOpen(true);
    };

    const handleUseMediaToggle = (e) => {
        const checked = e.target.checked;
        setDraftUseMedia(checked);
        if (!checked) {
            setDraftCategory("");
            setDraftAction({});
        }
    };

    const closeModal = () => {
        setErrors({});
        setModalIsOpen(false);
    };

    const handleCategoryChange = (e) => {
        const cat = e.target.value;
        setDraftCategory(cat);
        setDraftAction((prev) => ({ ...prev, prompt_category: cat, id: "", audio: "", name: "", duration: 0 }));
    };

    const handleAudioChange = (e) => {
        const selected = promptList.find(
            (a) => a.id.toString() === e.target.value
        );
        if (!selected) {
            setDraftAction((prev) => ({ ...prev, id: "", audio: "", name: "", duration: 0 }));
            return;
        }
        setDraftAction({
            type: "voicemail",
            prompt_category: draftCategory,
            id: selected.id,
            audio: selected.prompt_url,
            duration: selected.prompt_duration || 0,
            name: selected.prompt_name
        });
    };

    const handleDurationChange = (e) => {
        const val = e.target.value;
        if (/^\d*$/.test(val) && (val === "" || Number(val) <= VOICEMAIL_MAX_DURATION)) {
            setDraftDuration(val);
        }
    };

    const validate = () => {
        const newErrors = {};
        if (draftUseMedia) {
            if (draftCategory && !draftAction?.id) {
                newErrors.audio = "Audio is required when category is selected";
            }
        }
        if (!draftDuration || Number(draftDuration) < VOICEMAIL_MIN_DURATION) {
            newErrors.duration = `Min ${VOICEMAIL_MIN_DURATION}s`;
        } else if (Number(draftDuration) > VOICEMAIL_MAX_DURATION) {
            newErrors.duration = `Max ${VOICEMAIL_MAX_DURATION}s`;
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;
        updateNode({
            action: draftUseMedia ? draftAction : {},
            max_duration: Number(draftDuration),
            use_media: draftUseMedia
        });
        toast.success("Voicemail saved.");
        setModalIsOpen(false);
    };

    const filteredAudio = promptList.filter(
        (p) => !draftCategory || p.prompt_category === draftCategory
    );

    const handleRemoveNode = () => {
        if (data.parent === "gather Node" && data.idToRemove) {
            setNodes((nodes) =>
                nodes.map((node) => {
                    if (!node.data.selectedHandles) return node;
                    const updatedHandles = { ...node.data.selectedHandles };
                    const updatedSmsData = { ...node.data.smsData };
                    delete updatedHandles[data.idToRemove];
                    if (updatedSmsData) {
                        const parts = data.idToRemove.split("-");
                        const handleIndex = parts[parts.length - 1];
                        if (handleIndex) {
                            const smsKey = `DTMF_${handleIndex}`;
                            delete updatedSmsData[smsKey];
                        }
                    }
                    return {
                        ...node,
                        data: { ...node.data, selectedHandles: updatedHandles, smsData: updatedSmsData }
                    };
                })
            );
        }
        onRemove(id);
    };

    return (
        <div style={{ position: "relative", width: "90px", height: "40px" }}>
            <div
                className="ivr-flow-node"
                style={{
                    position: "absolute",
                    top: 8,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    border: "1.5px solid #6f42c1",
                    borderRadius: "10px 10px 0 10px",
                    background: "#fff",
                    zIndex: 3,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: "4px",
                    fontSize: "11px"
                }}
            >
                <Handle type="target" position="top" style={{ background: "#6f42c1" }} />

                <span
                    className="ivr-flow-node-label"
                    style={{ fontSize: "9px", fontWeight: "bold", textTransform: "capitalize" }}
                >
                    {data.label || "Voicemail"}
                </span>

                <div style={{ display: "flex", gap: "5px", paddingTop: "2px" }}>
                    <i
                        className="fa fa-cog ivr-flow-node-icon"
                        style={{ color: "#6f42c1", cursor: "pointer" }}
                        onClick={openModal}
                    />
                    <i
                        className="fa fa-trash ivr-flow-node-icon"
                        style={{ color: "#6f42c1", cursor: "pointer" }}
                        onClick={handleRemoveNode}
                    />
                    {audioUrl && (
                        <i
                            className="fa fa-play ivr-flow-node-icon"
                            style={{ color: "#28a745", cursor: "pointer" }}
                            title="Play audio"
                            onClick={() => setPlayModalOpen(true)}
                        />
                    )}
                </div>
            </div>

            <Modal size="lg" show={modalIsOpen} onHide={closeModal} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Voicemail</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group>
                            <Form.Label>Max Voicemail Duration (sec, {VOICEMAIL_MIN_DURATION}-{VOICEMAIL_MAX_DURATION})<Form.Text className="text-danger">*</Form.Text></Form.Label>
                            <Form.Control
                                type="number"
                                min={VOICEMAIL_MIN_DURATION}
                                max={VOICEMAIL_MAX_DURATION}
                                value={draftDuration}
                                onChange={handleDurationChange}
                                placeholder={`Enter duration in seconds (min ${VOICEMAIL_MIN_DURATION})`}
                            />
                            {errors.duration && <Form.Text className="text-danger">{errors.duration}</Form.Text>}
                        </Form.Group>

                        <Form.Group style={{ marginTop: 15 }}>
                            <Form.Check
                                type="checkbox"
                                id={`vm-use-media-${id}`}
                                label="Use Media"
                                checked={draftUseMedia}
                                onChange={handleUseMediaToggle}
                            />
                        </Form.Group>

                        {draftUseMedia && (
                            <>
                                <Form.Group style={{ marginTop: 15 }}>
                                    <Form.Label>Prompt Category</Form.Label>
                                    <Form.Control as="select" value={draftCategory} onChange={handleCategoryChange}>
                                        <option value="">Select Category</option>
                                        {VOICEMAIL_PROMPT_CATEGORIES.map((c) => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </Form.Control>
                                    {errors.category && <Form.Text className="text-danger">{errors.category}</Form.Text>}
                                </Form.Group>

                                <Form.Group style={{ marginTop: 15 }}>
                                    <Form.Label>Select Audio</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={draftAction?.id || ""}
                                        onChange={handleAudioChange}
                                        disabled={!draftCategory}
                                    >
                                        <option value="">Select Audio</option>
                                        {filteredAudio.map((item) => (
                                            <option key={item.id} value={item.id}>
                                                {item.prompt_name}
                                            </option>
                                        ))}
                                    </Form.Control>
                                    {errors.audio && <Form.Text className="text-danger">{errors.audio}</Form.Text>}
                                </Form.Group>
                            </>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={handleSave}>Save</Button>
                    <Button variant="secondary" onClick={closeModal}>Close</Button>
                </Modal.Footer>
            </Modal>

            <AudioPreviewModal
                show={playModalOpen}
                onClose={() => setPlayModalOpen(false)}
                audioUrl={audioUrl}
                title={audioName}
            />
        </div>
    );
};

export default VoicemailNode;
